/**
 * Service de cache IndexedDB pour les données d'initialisation
 * Permet de persister les données entre les sessions et d'afficher
 * immédiatement les données en cache pendant le chargement
 */

const DB_NAME = 'pos360-init-cache';
const DB_VERSION = 1;
const STORE_NAME = 'init-data';

interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  version: string;
}

interface InitCacheData {
  user: any;
  roles: any[];
  accesses: any[];
  supplies: any[];
  products: any[];
  users: any[];
  configSelected: any;
  meta: {
    cachedAt: number;
    version: string;
  };
}

let dbInstance: IDBDatabase | null = null;

/**
 * Ouvre la connexion à IndexedDB
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Sauvegarde les données d'initialisation dans le cache
 */
export const saveInitCache = async (data: InitCacheData): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const entry: CacheEntry<InitCacheData> = {
      key: 'init-data',
      data,
      timestamp: Date.now(),
      version: '1.0',
    };

    return new Promise((resolve, reject) => {
      const request = store.put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('[IndexedDB] Failed to save cache:', error);
  }
};

/**
 * Récupère les données d'initialisation depuis le cache
 */
export const getInitCache = async (): Promise<InitCacheData | null> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get('init-data');
      request.onsuccess = () => {
        const result = request.result as CacheEntry<InitCacheData> | undefined;
        if (result?.data) {
          // Vérifier si le cache n'est pas trop vieux (24 heures)
          const maxAge = 24 * 60 * 60 * 1000; // 24h
          const isValid = Date.now() - result.timestamp < maxAge;
          
          if (isValid) {
            resolve(result.data);
          } else {
            console.info('[IndexedDB] Cache expired, fetching fresh data');
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('[IndexedDB] Failed to get cache:', error);
    return null;
  }
};

/**
 * Efface le cache
 */
export const clearInitCache = async (): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete('init-data');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('[IndexedDB] Failed to clear cache:', error);
  }
};

/**
 * Met à jour une partie spécifique du cache (pour les données streamées)
 */
export const updateInitCachePartial = async <K extends keyof InitCacheData>(
  key: K,
  value: InitCacheData[K]
): Promise<void> => {
  try {
    const existing = await getInitCache();
    if (existing) {
      existing[key] = value;
      existing.meta.cachedAt = Date.now();
      await saveInitCache(existing);
    }
  } catch (error) {
    console.warn('[IndexedDB] Failed to update cache partially:', error);
  }
};

/**
 * Vérifie si le cache existe et est valide
 */
export const hasValidCache = async (): Promise<boolean> => {
  const cache = await getInitCache();
  return cache !== null;
};

export type { InitCacheData };
